import { Component, HostListener, OnInit } from '@angular/core';
import { ADN_PLUS } from '../../models/ADN_PLUS';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { Action } from '../../models';
import { breakpoints } from '../../environments/environment.breakpoints';
import { CtMessageStyle, CtMessageSize, CtMessageType, CtMessageVisualization } from '../../enums/plusMessage';
import { CtViewComponent } from '../ct-view/ct-view.component';

@Component({
  selector: 'ct-message',
  templateUrl: './ct-message.component.html',
  styleUrls: ['./ct-message.component.css']
})
/**
 * Responsible for displaying a customizable message to the user. 
 * The message has different styles, types, sizes, and visualizations, which can be configured to achieve the desired look and behavior. 
 * The component also supports the ability to trigger an event when clicked and can be closed if necessary.
 */
export class CtMessageComponent extends CtrlBaseComponent implements OnInit {

  //properties
  private style : CtMessageStyle;
  private type : CtMessageType;
  private size : CtMessageSize;
  private visualization : CtMessageVisualization;
  private title : string;
  private message : string;
  private messageEvent : Action;
  private actionIcon : string;
  private actionText : string;
  private canbeClosed : boolean = true;
  private styleClasses : string;
  
  //internal
  private value : ADN_PLUS; 
  private isExtraSmall : boolean = false;
  public isSmall : boolean = false;
  public isMedium : boolean = false;

  public iconWidth : number;
  public iconHeight : number;
  public isFullScreen : boolean = false;

  //constants
  public iconsUnicolor : Map<string,string> = new Map<string,string>([
    ['success', 'assets/icons/message-success.svg'],
    ['error', 'assets/icons/message-error.svg'],
  ]);

  public iconsMulticolor : Map<string,string> = new Map<string,string>([
    ['success', 'assets/icons/message-success-multicolor.svg'],
    ['error', 'assets/icons/message-error-multicolor.svg'],
  ]);

  //functions
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  constructor() { 
    super();
  }
  

  /**
   * The ngOnInit function initializes various properties and events in the component.
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.setIconSize();
    this.setMessageEvent();
    this.checkScreenSize();
    this.setDefaultFloat();
    this.checkIfFullScreen();
  }

  /**
   * When the screen if type "fullscreen" the message needs to be painted a bit lower on the screen
   */
  private checkIfFullScreen(): void {
    if(!this.utilService.currentView || this.getViewId() !== this.utilService.currentView.getViewCode()) return;
    const viewComponent : CtViewComponent = this.utilService.getComponentRef(this.getViewId()).view;
    this.isFullScreen = viewComponent.getIsFullScreen();
  }

  /**
   * This function checks the screen size and sets boolean variables based on the breakpoints defined.
   */
  private checkScreenSize() : void {
    const screenWidth = window.innerWidth;
    this.isMedium = screenWidth >= breakpoints.L;
    this.isSmall = screenWidth < breakpoints.L && screenWidth > breakpoints.M;
    this.isExtraSmall = screenWidth <= breakpoints.M && screenWidth > breakpoints.S;
  }

  /**
   * This function sets a message event to be triggered on a click event.
   */
  private setMessageEvent(): void {
    this.messageEvent = this.getEvent('onClick');
  }

  public hideMessage(): void {
    this.setHidden(true);
  }

  public showMessage(): void {
    this.setHidden(false);
  }

  private setDefaultFloat(): void {
    if(this.visualization === this.messageVisualization.float){
      this.setCanBeClosed(true);
    }
  }


  /**
   * This function returns an icon based on the message style and type.
   * @returns The method `getMessageIcon()` is returning a string value which is either the icon for the
   * message type in the unicolor style or the icon for the message type in the multicolor style,
   * depending on the current style set for the message.
   */
  public getMessageIcon(): string {
    if(this.getStyle() === CtMessageStyle.unicolor){
      return this.iconsUnicolor.get(this.getType());
    } else {
      return this.iconsMulticolor.get(this.getType());
    }
  }

  /**
   * Executes the event associated with the message.
   */
  public executeEvent(): void {
    let action = this.getEvent('onClick');
    if(action){
      this.executeAction2(action);
    }
  }

  /**
   * The function sets the size of an icon based on the message size.
   */
  private setIconSize(): void {
    if(this.getStyle() === CtMessageStyle.multicolor){
      this.iconWidth = 20;
      this.iconHeight = 20;
      return;
    }

    if(this.getSize() === CtMessageSize.large){
      this.iconWidth = 24;
      this.iconHeight = 28;
    } else {
      this.iconWidth = 33;
      this.iconHeight = 38.08;
    }
  }

  //getters for enums
  public get messageStyle(): typeof CtMessageStyle {
    return CtMessageStyle; 
  }

  public get messageType(): typeof CtMessageType {
    return CtMessageType; 
  }

  public get messageSize(): typeof CtMessageSize {
    return CtMessageSize; 
  }

  public get messageVisualization(): typeof CtMessageVisualization {
    return CtMessageVisualization; 
  }

  //getters and setters for properties
  getStyle(): CtMessageStyle { return this.style;}
  getType(): CtMessageType { return this.type;}
  getSize(): CtMessageSize { return this.size; }
  getVisualization(): CtMessageVisualization { return this.visualization; }
  getTitle(): string { return this.title; }
  getMessage(): string { return this.message; }
  getCanBeClosed(): boolean { return this.canbeClosed; }
  getActionIcon(): string { return this.actionIcon; }
  getActionText(): string { return this.actionText; }
  getValue(): ADN_PLUS { return this.value; }
  getStyleClasses(): string { return this.styleClasses; }
  getIsMedium(): boolean { return this.isMedium; }
  getIsSmall(): boolean { return this.isSmall; }
  getIsExtraSmall(): boolean { return this.isExtraSmall; }


  setStyle(style: CtMessageStyle): void { this.style = style}
  setType(type: CtMessageType): void { this.type = type }
  setSize(size: CtMessageSize): void { this.size = size}
  setVisualization(visualization: CtMessageVisualization): void { this.visualization = visualization}
  setTitle(title: string): void { this.title = title; }
  setMessage(message: string): void { this.message = message; }
  setCanBeClosed(canbeClosed: boolean): void { this.canbeClosed = canbeClosed; }
  setActionIcon(actionIcon: string): void { this.actionIcon = actionIcon; }
  setActionText(actionText: string): void { this.actionText = actionText; }
  setValue(value: ADN_PLUS): void { this.value = value; }
  setStyleClasses(styleClasses: string): void { this.styleClasses = styleClasses; }
  setIsSmall(isSmall: boolean): void { this.isSmall = isSmall; }
  setIsExtraSmall(isExtraSmall: boolean): void { this.isExtraSmall = isExtraSmall; }
  setIsMedium(isMedium: boolean): void { this.isMedium = isMedium; }

}